import Loader from "@components/global/Loader/Loader";
import MinerName from "@components/global/new/MinerName/MinerName";
import BorderIndication3 from "@mobx/UI/BorderIndication3/BorderIndication3";
import { useRackSectionSchemeDrag } from "@mobx/hooks/useRackSectionSchemeDrag";
import { useWatchNetwork } from "@mobx/services/http";
import mainLocationsStore from "@mobx/store/locations";
import { ItemStore } from "@mobx/store/locations/interfaces";
import { DRAGGING_TYPES3, IDragRackItemData3, IUnrackItem3, toRackItem3 } from "@mobx/store/locations/types";
import { useActiveRack } from "@utils/hooks/useActiveRack";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import { DragEvent, MouseEvent, useMemo, useRef, useState } from "react";
import styles from "./EmptyItem.module.scss";

type DragEventType = DragEvent<HTMLDivElement>;
export interface IEmptyItem3Props {
  itemStore: ItemStore;
};

const useControl = (props: IEmptyItem3Props) => {
  const draggingCounter = useRef(0);
  const locationStore = mainLocationsStore.activeLocation;
  const buildingStore = locationStore?.activeBuilding;
  const { item, isLoading, itemIndex, setLoading: onLoading } = props.itemStore;
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [outOfRange, setOutOfRange] = useState<boolean>(false);
  const [X, Y] = useMemo(() => [item.x, item.y], [item]);
  const rackStore = props.itemStore.rackStore;
  const self = useRef<HTMLDivElement | null>(null);
  const schemeDragItem = useRackSectionSchemeDrag(props.itemStore, self);
  useWatchNetwork(props.itemStore);

  const onDrop = async (e: DragEventType) => {
    e.preventDefault();
    const type = e.dataTransfer.items[0]?.type as DRAGGING_TYPES3;
    onLoading(true);

    switch (type) {
      case DRAGGING_TYPES3.RACK_ITEM:
        const rackItemData = JSON.parse(
          e.dataTransfer.getData(DRAGGING_TYPES3.RACK_ITEM)
        ) as IDragRackItemData3;
        
        if (!window.TRANSFER_RACK_ITEM) {
          console.error("ERROR with `window.TRANSFER_RACK_ITEM`");
          onLoading(false);
          return;
        }

        if (!await buildingStore?.placeAllocatedRackItem(props.itemStore, rackItemData, +rackItemData.prevRackId, rackStore.rack.id)) {
          onLoading(false);
          return;
        }
        window.TRANSFER_RACK_ITEM = undefined;
        break;

      case DRAGGING_TYPES3.UNRACK_ITEM:
        const data = JSON.parse(
          e.dataTransfer.getData(DRAGGING_TYPES3.UNRACK_ITEM)
        ) as IUnrackItem3;

        if (!await rackStore.placeUnallocatedRackItem(props.itemStore, data)) {
          onLoading(false);
          return;
        }
        break;
    }
    onLoading(false);
  };

  const onDragEnter = (e: DragEventType) => {
    const type = e.dataTransfer.items[0]?.type.toLowerCase();
    if (
      type !== DRAGGING_TYPES3.RACK_ITEM &&
      type !== DRAGGING_TYPES3.UNRACK_ITEM
    )
      return;
    e.preventDefault();
    setDragOver(true);
    draggingCounter.current++;
  };

  const onDragLeave = () => {
    draggingCounter.current--;
    setOutOfRange(false);
    setDragOver(false);
  };

  const onMouseEnterWhileMerging = () => {
    if (!window.DRAGING_FOR_MERGE) return;
    setDragOver(true);

    if (rackStore.rack.id !== +window.DRAGING_FOR_MERGE.rackId) {
      setOutOfRange(true);
      return;
    }
  };

  const onMouseLeaveWhileMerging = () => {
    setDragOver(false);
    setOutOfRange(false);
  };

  const onMouseUpWhileMerging = async () => {
    if (outOfRange) return;

    if (!window.DRAGING_FOR_MERGE) return;
    const draggedItem = window.DRAGING_FOR_MERGE.rackItem;

    onLoading(true);
    setDragOver(false);

    if (!await rackStore.mergeMinerCells(toRackItem3(draggedItem), window.DRAGING_FOR_MERGE.index, item.id, itemIndex)) {
      onLoading(false);
      return;
    }

    window.DRAGING_FOR_MERGE = undefined;
    onLoading(false);
  };

  const onSelectSection = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const activeSection = `A${Y}${X}`;
    buildingStore?.setActiveRackItemId(item.id);
    buildingStore?.setActiveIdentifiers(activeSection, rackStore.rack.rack_id);
  };

  const address = useMemo(() => {
    let coords = `${Y}${X}`;
    if (+X >= 10 || +Y >= 10) {
      coords = `${Y}-${X}`;
    }
    return `${locationStore?.location.Code ?? ""}-${buildingStore?.building.name ?? ""}-${rackStore.rack.rack_id}A${coords}`;
  }, [rackStore.rack, locationStore?.location.Code]);

  return {
    dragOver,
    outOfRange,
    loading: isLoading,
    address,
    shownAddress: `A${Y}${X}`,
    rack: rackStore.rack,
    activeRackItemID: buildingStore?.activeRackItemId,
    item,
    numSelectedUsers: locationStore?.selectedUsers.size ?? 0,
    self,
    isSelectedForSchemeDrag: schemeDragItem.isSelectedForSchemeDrag,
    dragID: schemeDragItem.dragID,

    onDrop,
    onDragEnter,
    onDragLeave,
    onMouseEnterWhileMerging,
    onMouseUpWhileMerging,
      onMouseLeaveWhileMerging,
      onSelectSection,
      mouseDown(e: React.MouseEvent<HTMLElement>) {
        if (!e.ctrlKey) return;
        schemeDragItem.mouseDown(e);
        e.stopPropagation();
        e.preventDefault();
      },
  };
};

const EmptyItem3 = observer((props: IEmptyItem3Props) => {
  const ctrl = useControl(props);
  const activeRack = useActiveRack();

  return (
    <div
      tabIndex={0}
      className={cn(styles.empty_item, {
        [styles.selected]:
          activeRack.rack === ctrl.rack.rack_id &&
          activeRack.cell === ctrl.shownAddress,
        [styles.not_filtered]: ctrl.numSelectedUsers > 0,
        [styles.draggingOver]: ctrl.dragOver,
        [styles.highlighted]: ctrl.activeRackItemID === ctrl.item.id,
        [styles.selectedForSchemeDrag]: ctrl.isSelectedForSchemeDrag,
      })}
      ref={ctrl.self}
      onDragEnter={ctrl.onDragEnter}
      onDrop={ctrl.onDrop}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      onDragLeave={ctrl.onDragLeave}
      onMouseEnter={ctrl.onMouseEnterWhileMerging}
      onMouseUp={ctrl.onMouseUpWhileMerging}
      onMouseLeave={ctrl.onMouseLeaveWhileMerging}
      onClick={ctrl.onSelectSection}
      onMouseDownCapture={ctrl.mouseDown}
      data-drag-id={ctrl.dragID}
    >
      {ctrl.loading && <Loader size={16} style={{ borderRadius: 3 }} />}
      {ctrl.dragOver && (
        <BorderIndication3
          size={window.TRANSFER_RACK_ITEM?.size || []}
          rangeError={ctrl.outOfRange}
        />
      )}

      <MinerName fullAddress={ctrl.address} useFullName canCopy>
        {ctrl.shownAddress}
      </MinerName>
    </div>
  );
});

EmptyItem3.displayName = "EmptyItem3";

export default EmptyItem3;
